import React, { useState } from 'react';
import { Cpu, ShieldCheck, CheckCircle2, XCircle, AlertCircle, ExternalLink } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/context/I18nContext';
import { useToast } from '@/context/ToastContext';
import { rpc } from '@/api/client';
import { getGoogleClientId, getAppleClientId } from '@/lib/config';
import { redirectToGoogleAuth, redirectToAppleAuth } from '@shared/auth/oauth';

function GoogleLogo({ size = 18 }: { size?: number }) {
  return (
    <svg viewBox="0 0 48 48" width={size} height={size}>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59A14.5 14.5 0 019.5 24c0-1.59.28-3.14.76-4.59l-7.98-6.19A23.99 23.99 0 000 24c0 3.77.9 7.35 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}

function AppleLogo({ size = 18 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.37c.61-.75 1.04-1.8.92-2.85-.9.04-2 .6-2.64 1.35-.56.65-.99 1.7-.87 2.71 1.01.08 1.98-.46 2.59-1.21z" />
    </svg>
  );
}

interface OAuthConsentPageProps {
  params: Record<string, string>;
}

export function OAuthConsentPage({ params }: OAuthConsentPageProps) {
  const { user } = useAuth();
  const { t } = useI18n();
  const { showToast } = useToast();

  const [isApproving, setIsApproving] = useState(false);

  const clientId = params.client_id || 'ai-client';
  const redirectUri = params.redirect_uri || '';
  const codeChallenge = params.code_challenge || '';
  const codeChallengeMethod = params.code_challenge_method || 'S256';
  const scope = params.scope || 'telemetry:read';
  const state = params.state || '';

  const clientName = (() => {
    const lower = clientId.toLowerCase();
    if (lower.includes('antigravity')) return 'Antigravity';
    if (lower.includes('claude')) return 'Claude Desktop';
    if (lower.includes('cursor')) return 'Cursor';
    if (lower.includes('zed')) return 'Zed';
    if (lower.includes('windsurf')) return 'Windsurf';
    if (lower.includes('cline')) return 'Cline';
    return clientId || 'AI Assistant';
  })();

  const handleAuthorize = async () => {
    if (!redirectUri || !codeChallenge) {
      showToast(t('oauth.missingParams'), 'error');
      return;
    }

    setIsApproving(true);
    try {
      const res = await rpc<{ redirectUrl: string }>('ApproveOAuthAuthorization', {
        clientId,
        redirectUri,
        codeChallenge,
        codeChallengeMethod,
        scope,
        state,
      });

      if (res?.redirectUrl) {
        window.location.href = res.redirectUrl;
      }
    } catch (err: any) {
      showToast(err?.message || 'Failed to approve authorization', 'error');
      setIsApproving(false);
    }
  };

  const handleCancel = () => {
    if (redirectUri) {
      const sep = redirectUri.includes('?') ? '&' : '?';
      let cancelUrl = `${redirectUri}${sep}error=access_denied&error_description=User+denied+access`;
      if (state) {
        cancelUrl += `&state=${encodeURIComponent(state)}`;
      }
      window.location.href = cancelUrl;
    } else {
      window.location.hash = '#/';
    }
  };

  const handleLoginGoogle = () => {
    const cid = getGoogleClientId();
    if (!cid) return;
    redirectToGoogleAuth(cid, window.location.hash);
  };

  const handleLoginApple = () => {
    const cid = getAppleClientId();
    if (!cid) return;
    redirectToAppleAuth(cid, window.location.hash);
  };

  return (
    <div className="flex-1 flex items-center justify-center p-4 bg-bg-base">
      <div className="card w-full max-w-md p-6 space-y-6 shadow-2xl border border-border-default bg-bg-raised">
        {/* Header Icon & Title */}
        <div className="text-center space-y-2">
          <div className="inline-flex p-3 rounded-xl bg-accent/10 text-accent mb-1">
            <Cpu size={28} />
          </div>
          <h2 className="text-lg font-bold text-text-primary">
            {t('oauth.authorizeTitle', { name: clientName })}
          </h2>
          <p className="text-xs text-text-secondary">
            {t('oauth.authorizeSubtitle', { name: clientName })}
          </p>
        </div>

        {/* Permissions list */}
        <div className="p-4 rounded-lg bg-bg-surface border border-border-subtle space-y-3">
          <div className="text-xs font-semibold text-text-primary flex items-center gap-1.5">
            <ShieldCheck size={14} className="text-accent" />
            <span>{t('oauth.permissionsTitle')}</span>
          </div>
          <ul className="space-y-2 text-xs text-text-secondary">
            <li className="flex items-start gap-2">
              <CheckCircle2 size={14} className="text-emerald-400 shrink-0 mt-0.5" />
              <span>{t('oauth.permSessions')}</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 size={14} className="text-emerald-400 shrink-0 mt-0.5" />
              <span>{t('oauth.permAnalysis')}</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 size={14} className="text-emerald-400 shrink-0 mt-0.5" />
              <span>{t('oauth.permSetups')}</span>
            </li>
          </ul>
        </div>

        {/* User Account / Sign In */}
        {user ? (
          <div className="p-3 rounded-lg bg-bg-surface border border-border-subtle flex items-center justify-between text-xs">
            <div className="flex items-center gap-2 overflow-hidden">
              <div className="w-7 h-7 rounded-full bg-accent/20 text-accent font-semibold flex items-center justify-center shrink-0">
                {user.name.charAt(0).toUpperCase()}
              </div>
              <div className="truncate">
                <div className="font-medium text-text-primary truncate">{user.name}</div>
                <div className="text-[0.7rem] text-text-muted truncate">{user.email}</div>
              </div>
            </div>
            <span className="text-[0.65rem] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-medium">
              {t('oauth.signedIn')}
            </span>
          </div>
        ) : (
          <div className="space-y-3 text-center">
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs flex items-center gap-2 text-left">
              <AlertCircle size={16} className="shrink-0" />
              <span>{t('oauth.mustSignInFirst')}</span>
            </div>
            <div className="flex flex-col gap-2 pt-1">
              <button
                type="button"
                onClick={handleLoginGoogle}
                className="w-full flex items-center justify-center gap-2.5 py-2 px-4 rounded-md bg-white hover:bg-neutral-100 text-neutral-800 text-xs font-semibold shadow-xs transition-colors cursor-pointer"
              >
                <GoogleLogo size={16} />
                <span>{t('auth.signInWithGoogle')}</span>
              </button>
              <button
                type="button"
                onClick={handleLoginApple}
                className="w-full flex items-center justify-center gap-2.5 py-2 px-4 rounded-md bg-black hover:bg-neutral-900 text-white text-xs font-semibold border border-neutral-700 shadow-xs transition-colors cursor-pointer"
              >
                <AppleLogo size={16} />
                <span>{t('auth.signInWithApple')}</span>
              </button>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {user && (
          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={handleCancel}
              disabled={isApproving}
              className="flex-1 py-2 rounded-md text-xs font-medium text-text-secondary hover:text-text-primary border border-border-default hover:bg-bg-hover transition-colors cursor-pointer text-center"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleAuthorize}
              disabled={isApproving || !redirectUri || !codeChallenge}
              className="flex-1 py-2 rounded-md text-xs font-semibold bg-accent text-white hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer flex items-center justify-center gap-1.5 shadow-sm"
            >
              {isApproving ? t('common.loading') : t('oauth.authorizeButton')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
